import React from "react";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Clock, XCircle } from "lucide-react";
import { Link, useParams } from "react-router-dom";

export const SubscriptionBanner = () => {
  const { company, user } = useAuth();
  const { slug } = useParams();

  // Don't show for super admins
  if (!company || !user || user.role === "super_admin") {
    return null;
  }

  const subscriptionEnd = company.subscription_end ? new Date(company.subscription_end) : null;
  const now = new Date();
  const subscriptionStatus = company.subscription_status;

  // Calculate days until expiration or days past due
  let daysRemaining = 0;
  let daysPastDue = 0;
  
  if (subscriptionEnd) {
    const diffTime = subscriptionEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      daysRemaining = diffDays;
    } else {
      daysPastDue = Math.abs(diffDays);
    }
  }

  // Don't show banner if subscription is active and more than 7 days remaining
  if (subscriptionStatus === "active" && daysRemaining > 7) {
    return null;
  }

  // Don't show for suspended accounts (they'll be redirected anyway)
  if (subscriptionStatus === "suspended") {
    return null;
  }

  // Determine banner type and content
  let bannerConfig = null;

  if (subscriptionStatus === "expired" || (subscriptionStatus === "active" && daysPastDue > 0)) {
    // Expired or past due
    const graceDaysLeft = 5 - daysPastDue;
    
    if (graceDaysLeft > 0) {
      bannerConfig = {
        type: "error",
        icon: XCircle,
        title: "Tu suscripción ha vencido",
        message: `Tienes ${graceDaysLeft} día${graceDaysLeft !== 1 ? 's' : ''} para renovar antes de que tu cuenta sea suspendida.`,
        bgColor: "bg-red-600",
        textColor: "text-white",
        buttonBg: "bg-white text-red-600 hover:bg-red-50"
      };
    } else {
      bannerConfig = {
        type: "critical",
        icon: XCircle,
        title: "Cuenta próxima a suspenderse",
        message: "Tu período de gracia ha terminado. Tu cuenta será suspendida en las próximas horas.",
        bgColor: "bg-red-800",
        textColor: "text-white",
        buttonBg: "bg-white text-red-800 hover:bg-red-50"
      };
    }
  } else if (daysRemaining <= 7 && daysRemaining > 3) {
    // 4-7 days remaining
    bannerConfig = {
      type: "warning",
      icon: Clock,
      title: "Tu suscripción vence pronto",
      message: `Tu suscripción vence en ${daysRemaining} días. Renueva para evitar interrupciones.`,
      bgColor: "bg-amber-500",
      textColor: "text-white",
      buttonBg: "bg-white text-amber-600 hover:bg-amber-50"
    };
  } else if (daysRemaining <= 3 && daysRemaining > 1) {
    // 2-3 days remaining
    bannerConfig = {
      type: "urgent",
      icon: AlertTriangle,
      title: "Renovación urgente",
      message: `Tu suscripción vence en ${daysRemaining} días. ¡Renueva ahora!`,
      bgColor: "bg-orange-500",
      textColor: "text-white",
      buttonBg: "bg-white text-orange-600 hover:bg-orange-50"
    };
  } else if (daysRemaining === 1) {
    // 1 day remaining
    bannerConfig = {
      type: "critical",
      icon: AlertTriangle,
      title: "¡Último día!",
      message: "Tu suscripción vence mañana. Renueva hoy para evitar la suspensión.",
      bgColor: "bg-red-500",
      textColor: "text-white",
      buttonBg: "bg-white text-red-600 hover:bg-red-50"
    };
  } else if (daysRemaining === 0) {
    // Expires today
    bannerConfig = {
      type: "critical",
      icon: XCircle,
      title: "Tu suscripción vence hoy",
      message: "Renueva ahora para mantener tu acceso sin interrupciones.",
      bgColor: "bg-red-600",
      textColor: "text-white",
      buttonBg: "bg-white text-red-600 hover:bg-red-50"
    };
  }

  if (!bannerConfig) {
    return null;
  }

  const Icon = bannerConfig.icon;

  return (
    <div className={`${bannerConfig.bgColor} ${bannerConfig.textColor} px-4 py-3 shadow-lg`}>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 flex-shrink-0" />
          <div>
            <span className="font-semibold">{bannerConfig.title}</span>
            <span className="hidden sm:inline"> — </span>
            <span className="block sm:inline text-sm opacity-90">{bannerConfig.message}</span>
          </div>
        </div>
        <Link
          to={`/empresa/${slug}/subscription`}
          className={`${bannerConfig.buttonBg} px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0`}
        >
          Renovar ahora
        </Link>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
